/**
 * AxisLineAnnotation — Axis alignment goal annotation panel
 *
 * Simplified, coach-friendly interface for defining body axis alignment goals.
 * Two modes: preset (Torso Lean) or custom (draw on video). The axis line
 * is rendered on the video canvas by AnnotationCanvasOverlay — this panel
 * only configures it.
 *
 * Design: Industrial minimalism — preset cards at top, settings on demand.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { ArrowUpIcon, ChevronDownIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import AnnotationPanel from './AnnotationPanel';
import type { AxisConfig, AxisReferenceType, Phase, TemplateAnchor } from '@/types/api';
import FunctionalLandmarkPicker from './FunctionalLandmarkPicker';
import type { PoseData } from '@/utils/landmarkDetection';
import { computeMeanAxisAngle } from '@/utils/axisAngleUtils';
import Tooltip from '@/components/ui/Tooltip';
import { SO_LIBRARY, soImportanceToSlider } from './stanceOrientationLibrary';
import StrictnessControl from './StrictnessControl';
import GateBreachControl from './GateBreachControl';
import {
  HARD_LIMIT_TOGGLE_EXPLANATION,
  HARD_LIMIT_TOGGLE_LABEL,
} from '@/utils/gateCopy';
import type { GateSummary } from '@/types/api';

// ─── Presets ────────────────────────────────────────────────────────────────
// Grouped by usage context. Virtual/computed landmarks (mid_hip, mid_shoulder,
// etc.) are resolved by the backend at evaluation time.

const PRESETS = [
  // ─── Universal ──────────────────────────────────────
  {
    id: 'torso_lean',
    label: 'Torso Lean',
    hint: 'Lead hip to lead shoulder vs vertical',
    landmark_start: 'lead_hip',
    landmark_end: 'lead_shoulder',
    reference_type: 'vertical' as AxisReferenceType,
    target_angle_degrees: 0,
  },
  {
    id: 'trunk_line',
    label: 'Trunk Line',
    hint: 'Center hip to center shoulder',
    landmark_start: 'mid_hip',
    landmark_end: 'mid_shoulder',
    reference_type: 'vertical' as AxisReferenceType,
    target_angle_degrees: 0,
  },
  {
    id: 'plumb_line',
    label: 'Plumb Line',
    hint: 'Full body vertical alignment',
    landmark_start: 'mid_ankle',
    landmark_end: 'mid_ear',
    reference_type: 'vertical' as AxisReferenceType,
    target_angle_degrees: 0,
  },
  // ─── Combat / Striking ──────────────────────────────
  {
    id: 'hip_shoulder_separation',
    label: 'X-Factor',
    hint: 'Hip-shoulder rotation difference',
    landmark_start: 'mid_hip',
    landmark_end: 'mid_shoulder',
    reference_type: 'template_angle' as AxisReferenceType,
    target_angle_degrees: 0,
  },
  {
    id: 'arm_extension',
    label: 'Arm Extension',
    hint: 'Lead shoulder to lead wrist',
    landmark_start: 'lead_shoulder',
    landmark_end: 'lead_wrist',
    reference_type: 'template_angle' as AxisReferenceType,
    target_angle_degrees: 0,
  },
  // ─── Stance / Balance ───────────────────────────────
  {
    id: 'hip_level',
    label: 'Hip Level',
    hint: 'Lead to trail hip alignment',
    landmark_start: 'lead_hip',
    landmark_end: 'trail_hip',
    reference_type: 'horizontal' as AxisReferenceType,
    target_angle_degrees: 0,
  },
  {
    id: 'shoulder_level',
    label: 'Shoulder Level',
    hint: 'Lead to trail shoulder alignment',
    landmark_start: 'lead_shoulder',
    landmark_end: 'trail_shoulder',
    reference_type: 'horizontal' as AxisReferenceType,
    target_angle_degrees: 0,
  },
  {
    id: 'foot_level',
    label: 'Foot Level',
    hint: 'Lead heel to trail heel alignment',
    landmark_start: 'lead_heel',
    landmark_end: 'trail_heel',
    reference_type: 'horizontal' as AxisReferenceType,
    target_angle_degrees: 0,
  },
  // ─── Rotation / 3D ──────────────────────────────────
  {
    id: 'stance_orientation',
    label: 'Stance Orientation',
    hint: 'Which way the torso should face relative to the camera — in profile (a shoulder toward the lens) vs chest or back toward the lens. Uses 3D pose data when available.',
    landmark_start: '',
    landmark_end: '',
    reference_type: 'template_angle' as AxisReferenceType,
    target_angle_degrees: 0,
    use_rotation_angle: true,
  },
  // ─── Custom ─────────────────────────────────────────
  {
    id: 'custom',
    label: 'Custom Axis',
    hint: 'Draw directly on the video',
    landmark_start: '',
    landmark_end: '',
    reference_type: 'template_angle' as AxisReferenceType,
    target_angle_degrees: 0,
  },
] as const;

type PresetId = typeof PRESETS[number]['id'] | null;

// ─── Exports ────────────────────────────────────────────────────────────────

export interface AxisAnchors {
  start: TemplateAnchor | null;
  end: TemplateAnchor | null;
}

interface AxisLineAnnotationProps {
  // Panel state
  isExpanded: boolean;
  onToggle: () => void;
  panelNumber?: number;
  totalPanels?: number;

  // Axis goal state
  axisConfig: AxisConfig | undefined;
  onAxisConfigChange: (config: AxisConfig | undefined) => void;

  // Phase context
  phases?: Phase[];
  currentPhase?: Phase | null;

  // Save callback
  onSave: (config: AxisConfig, label: string, notes: string, importance: number) => Promise<void>;

  // Landmark selection callbacks
  onRequestLandmarkSelection?: (
    which: 'landmark_start' | 'landmark_end',
    callback: (landmarkIndex: number, landmarkName: string) => void
  ) => void;
  isSelectingLandmark?: 'landmark_start' | 'landmark_end' | null;

  // Anchor mode props
  anchorMode?: boolean;
  onToggleAnchorMode?: () => void;
  anchors?: AxisAnchors | null;
  onResetAnchors?: () => void;
  pendingAnchorPoint?: { x: number; y: number } | null;

  // Status
  isSaved?: boolean;
  isSelectionActive?: boolean;

  /**
   * All template pose frames keyed by frame_number.
   * Used to compute and display the measured template angle when
   * reference_type === 'template_angle', so the coach knows what the
   * default resolves to before saving.  Optional — the angle display
   * is simply omitted when not provided.
   */
  templatePoseFrames?: Map<number, PoseData>;

  /**
   * PR-5. The backend's verdict for the check being edited — what it does when
   * the athlete is off, and WHOSE number decides it. Threaded from the
   * checkpoint being edited; `null`/absent on a brand-new check, which the
   * hard-limit control then describes from its own unsaved state.
   *
   * Read-only here: this panel never writes a shape or a threshold source, it
   * only writes the number the coach types (`coach_breach_degrees`).
   */
  gateSummary?: GateSummary | null;

  /**
   * Goal F (EXP-010 A7b, step F4). The coach-typed pricing limit for the
   * body-position check (metadata.pricing_coach_limits.A_orientation.limit,
   * in degrees), when one exists. Read-only here — this panel never writes
   * it (that lives on the calibration page, PricingCoachLimitsFields.tsx).
   * Used ONLY to warn when it disagrees with `coach_breach_degrees` (the
   * hard-limit angle this panel DOES write) — while a hard limit lives on
   * this check, `coach_breach_degrees` keeps precedence at scoring time
   * (CLAUDE.md "Adding a New Goal Evaluator" §11); the warning exists so a
   * coach who typed a pricing limit elsewhere notices the two numbers do
   * not match. `undefined`/`null` (no typed pricing limit, or the caller
   * has not wired it) renders no warning — this is the current state from
   * every caller until a future step threads it from the calibration page.
   */
  pricingOrientationLimitDegrees?: number | null;

  className?: string;
}

/** Goal F step F4: two numbers meant to describe the same line are treated
 * as a mismatch past a tiny float-noise margin, not at exact equality. */
const PRICING_LIMIT_MISMATCH_EPSILON_DEGREES = 0.01;

// B-2026-0031: creation default is TEMPLATE_ANGLE so new axis goals score
// deviation from what the coach demonstrated, not from an absolute vertical.
// The backend model default (spatial_goals.py AxisConfig.reference_type) stays
// VERTICAL so existing stored templates continue to deserialize and score as
// before — only newly created configs get this explicit TEMPLATE_ANGLE value.
const defaultAxisConfig: AxisConfig = {
  enabled: true,
  landmark_start: '',
  landmark_end: '',
  reference_type: 'template_angle' as AxisReferenceType,
  target_angle_degrees: 0.0,
  angle_tolerance_degrees: 10.0,
  measure_stability: true,
  stability_threshold: 5.0,
};

// ─── Component ──────────────────────────────────────────────────────────────

export default function AxisLineAnnotation({
  isExpanded,
  onToggle,
  panelNumber = 4,
  totalPanels = 4,
  axisConfig,
  onAxisConfigChange,
  phases: _phases,
  currentPhase,
  onSave,
  onRequestLandmarkSelection,
  isSelectingLandmark: _isSelectingLandmark = null,
  anchorMode = false,
  onToggleAnchorMode,
  anchors = null,
  onResetAnchors,
  pendingAnchorPoint = null,
  isSaved = false,
  isSelectionActive = false,
  templatePoseFrames,
  gateSummary = null,
  pricingOrientationLimitDegrees = null,
  className,
}: AxisLineAnnotationProps) {
  // ── Local state ─────────────────────────────────────────────────────────
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [importance, setImportance] = useState(50);
  const [isSaving, setIsSaving] = useState(false);
  const [localIsSaved, setLocalIsSaved] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activePreset, setActivePreset] = useState<PresetId>(null);
  // Rotation mode local state
  const [rotationReferenceType, setRotationReferenceType] = useState<'template_angle' | 'fixed_angle'>('template_angle');
  const [rotationTargetAngle, setRotationTargetAngle] = useState(0);
  const [rotationTolerance, setRotationTolerance] = useState(15);
  const [rotationIsGate, setRotationIsGate] = useState(false);
  // N6e — Axis purpose (coach-authored; opaque; never side-swapped)
  const [purposeCategory, setPurposeCategory] = useState<'teaching' | 'biomechanical' | ''>('');
  const [purposeText, setPurposeText] = useState('');

  // ── Measured template angle ─────────────────────────────────────────────
  // When reference_type === 'template_angle', compute the mean axis angle
  // from the loaded template pose frames so the coach can see what the
  // default resolves to before saving.  Functional landmarks (lead_*/trail_*)
  // are skipped (they depend on handedness, resolved at eval time).
  const measuredAngle = useMemo(() => {
    if (axisConfig?.reference_type !== 'template_angle') return null;
    if (!templatePoseFrames) return null;
    const start = axisConfig?.landmark_start;
    const end = axisConfig?.landmark_end;
    if (!start || !end) return null;
    return computeMeanAxisAngle(
      templatePoseFrames,
      start,
      end,
      currentPhase?.start_frame,
      currentPhase?.end_frame,
    );
  }, [
    axisConfig?.reference_type,
    axisConfig?.landmark_start,
    axisConfig?.landmark_end,
    templatePoseFrames,
    currentPhase?.start_frame,
    currentPhase?.end_frame,
  ]);

  // ── Derived state ───────────────────────────────────────────────────────
  /**
   * PR-5 — the failure angle the coach typed, read straight off the config
   * rather than mirrored into local state.
   *
   * Same shape as `StrictnessControl`'s number: it lives in `axisConfig`, so
   * opening an existing check for editing shows the number the coach already
   * set (`handleEdit` hydrates `axisConfig`), and the parent's post-save
   * `setAxisConfig(undefined)` clears it for the next check. A local copy would
   * have needed both of those behaviours re-implemented, and would have gone
   * stale the moment the two disagreed.
   *
   * `null` means no failure angle is set, which is a real answer: the check
   * grades rather than fails, and nothing substitutes a default (§C.3c).
   */
  const rotationBreachDegrees = axisConfig?.coach_breach_degrees ?? null;

  // Goal F (EXP-010 A7b, step F4): while a hard limit lives on this check,
  // coach_breach_degrees keeps precedence at scoring (CLAUDE.md §11) — this
  // is purely a coach-facing nudge to keep the two numbers in sync, never a
  // second source of truth.
  const pricingLimitMismatch =
    rotationBreachDegrees != null &&
    pricingOrientationLimitDegrees != null &&
    Math.abs(rotationBreachDegrees - pricingOrientationLimitDegrees) >
      PRICING_LIMIT_MISMATCH_EPSILON_DEGREES;

  const isCustomMode = activePreset === 'custom';
  const isRotationMode = activePreset === 'stance_orientation' || axisConfig?.use_rotation_angle === true;
  const anchorsReady = anchorMode && !!anchors?.start && !!anchors?.end;
  const landmarksReady = !anchorMode && !!axisConfig?.landmark_start && !!axisConfig?.landmark_end;
  const hasCompleteAxisConfig = !!(axisConfig?.enabled && (landmarksReady || anchorsReady || isRotationMode));
  const canSave = hasCompleteAxisConfig && !!label.trim();

  // ── Auto-label on preset change (not on landmark names) ─────────────────
  const presetLabels: string[] = PRESETS.map((p) => p.label);
  useEffect(() => {
    if (activePreset === 'custom' && anchorsReady) {
      if (!label || presetLabels.includes(label)) {
        setLabel('Custom Axis');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePreset, anchorsReady, label]);

  // ── Callbacks ───────────────────────────────────────────────────────────

  const updateAxisConfig = useCallback(
    (field: keyof AxisConfig, value: AxisConfig[keyof AxisConfig]) => {
      onAxisConfigChange({
        ...(axisConfig || defaultAxisConfig),
        [field]: value,
      });
    },
    [axisConfig, onAxisConfigChange]
  );

  const selectPreset = useCallback(
    (presetId: PresetId) => {
      const preset = PRESETS.find((p) => p.id === presetId);
      if (!preset) return;

      setActivePreset(presetId);
      setShowAdvanced(false);

      if (preset.id === 'custom') {
        // Enter anchor mode — clear any landmark-based config
        if (!anchorMode) onToggleAnchorMode?.();
        onAxisConfigChange({
          ...(axisConfig || defaultAxisConfig),
          landmark_start: '',
          landmark_end: '',
          reference_type: preset.reference_type,
          target_angle_degrees: preset.target_angle_degrees,
          use_rotation_angle: undefined,
          is_gate: undefined,
          // The failure angle belongs to the body-facing hard limit and to no
          // other shape (§C.3c). Clearing it with `is_gate` stops a number the
          // coach typed on a hard limit riding along on an ordinary check.
          coach_breach_degrees: undefined,
        });
        setLabel('Custom Axis');
      } else if (preset.id === 'stance_orientation') {
        // Rotation mode — no landmarks or anchors needed
        if (anchorMode) onToggleAnchorMode?.();
        onResetAnchors?.();
        onAxisConfigChange({
          ...(axisConfig || defaultAxisConfig),
          landmark_start: '',
          landmark_end: '',
          reference_type: 'template_angle' as AxisReferenceType,
          target_angle_degrees: 0,
          use_rotation_angle: true,
          measure_stability: false,
        });
        setLabel(preset.label);
      } else {
        // Exit anchor mode if active, apply landmark preset
        if (anchorMode) onToggleAnchorMode?.();
        onResetAnchors?.();
        onAxisConfigChange({
          ...(axisConfig || defaultAxisConfig),
          landmark_start: preset.landmark_start,
          landmark_end: preset.landmark_end,
          reference_type: preset.reference_type,
          target_angle_degrees: preset.target_angle_degrees,
          use_rotation_angle: undefined,
          is_gate: undefined,
          coach_breach_degrees: undefined,
        });
        setLabel(preset.label);
      }
    },
    [axisConfig, onAxisConfigChange, anchorMode, onToggleAnchorMode, onResetAnchors]
  );

  const handleRequestLandmarkSelection = useCallback(
    (which: 'landmark_start' | 'landmark_end') => {
      if (onRequestLandmarkSelection) {
        onRequestLandmarkSelection(which, (_idx: number, name: string) => {
          onAxisConfigChange({
            ...(axisConfig || defaultAxisConfig),
            [which]: name,
          });
          setActivePreset(null); // Manual selection = no preset
          if (!label || PRESETS.some((p) => p.label === label)) {
            setLabel('Custom Axis');
          }
        });
      }
    },
    [onRequestLandmarkSelection, axisConfig, onAxisConfigChange, label]
  );

  const handleSave = useCallback(async () => {
    if (!hasCompleteAxisConfig || !label.trim()) return;

    // N6e: embed purpose fields into the axis config before saving.
    // These are opaque coach-authored strings — never side-swapped or regex-transformed.
    const purposeFields: Partial<AxisConfig> = {
      purpose_category: purposeCategory || null,
      purpose_text: purposeText.trim() || null,
    };

    let configToSave = axisConfig || defaultAxisConfig;
    if (isRotationMode) {
      configToSave = {
        ...configToSave,
        enabled: true,
        use_rotation_angle: true,
        // Goal F (EXP-010 A7b, step F4) — DECOUPLED from is_gate below.
        // rotation_profile=true just means "this IS a Stance Orientation
        // goal" — it activates the body-position MEASUREMENT (the
        // rotation_profile_median the pricing hook and its day-one path
        // read), independent of whether the auto-fail checkbox is ticked.
        // Previously this was `rotationIsGate ? true : undefined`, which
        // meant unticking auto-fail silently stopped the measurement from
        // reaching results at all — the one thing a coach retiring the gate
        // in favour of a typed pricing limit needs to KEEP. NEVER surface
        // this field name in the UI.
        //
        // F-6 (red team): PRESERVE, don't force. The gate ticked (rotationIsGate)
        // always sets it true. With the gate unticked, it stays true ONLY when
        // the loaded annotation already had rotation_profile===true — i.e. the
        // coach is retiring an existing Stance Orientation gate and keeping the
        // measurement alive. A rotation-mode goal that never had this field
        // (any stance_orientation preset or use_rotation_angle===true goal
        // authored without a gate) must NOT gain it on save — doing so would
        // redefine `passed` for that goal in body_rotation.py and turn it into
        // an injection target it was never meant to be.
        rotation_profile:
          rotationIsGate || axisConfig?.rotation_profile === true ? true : undefined,
        // is_gate still follows the checkbox exactly as before — this is
        // the ONLY field the hard-limit toggle controls now.
        is_gate: rotationIsGate,
        // PR-5 — the coach's failure angle, verbatim: the number they typed is
        // the number the scorer compares against (§C.3c's unit rule). It is
        // sent ONLY on a hard limit; with the hard limit off there is no
        // failure line to describe, so the key is dropped rather than left at
        // a stale value from an earlier edit. `null` on a hard limit is
        // meaningful — "no failure angle set" — and is not a default.
        coach_breach_degrees: rotationIsGate ? rotationBreachDegrees : undefined,
        // (read from `axisConfig` via `rotationBreachDegrees` — see its note)
        reference_type: rotationReferenceType as AxisReferenceType,
        target_angle_degrees: rotationReferenceType === 'fixed_angle' ? rotationTargetAngle : 0,
        angle_tolerance_degrees: rotationTolerance,
        landmark_start: '',
        landmark_end: '',
        measure_stability: false,
        anchor_start: undefined,
        anchor_end: undefined,
        ...purposeFields,
      };
    } else if (anchorsReady && anchors?.start && anchors?.end) {
      configToSave = {
        ...configToSave,
        landmark_start: '',
        landmark_end: '',
        anchor_start: anchors.start,
        anchor_end: anchors.end,
        ...purposeFields,
      };
    } else {
      configToSave = { ...configToSave, ...purposeFields };
    }

    setIsSaving(true);
    try {
      await onSave(configToSave, label.trim(), notes.trim(), importance);
      setLabel('');
      setNotes('');
      setImportance(50);
      setActivePreset(null);
      setShowSettings(false);
      setShowAdvanced(false);
      setPurposeCategory('');
      setPurposeText('');
      setLocalIsSaved(true);
      setTimeout(() => setLocalIsSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save axis alignment annotation:', error);
    } finally {
      setIsSaving(false);
    }
  }, [
    axisConfig, anchors, anchorsReady, hasCompleteAxisConfig, label, notes, importance, onSave,
    isRotationMode, rotationIsGate, rotationBreachDegrees, rotationReferenceType,
    rotationTargetAngle, rotationTolerance,
    purposeCategory, purposeText,
  ]);

  // ── Preset button helper ─────────────────────────────────────────────────
  // Extracted to avoid repeating the same JSX across the grouped grid sections.
  const renderPresetButton = (preset: typeof PRESETS[number]) => {
    const isActive =
      preset.id === 'custom'
        ? activePreset === 'custom' || (anchorMode && activePreset !== 'torso_lean')
        : activePreset === preset.id;

    return (
      <button
        key={preset.id}
        type="button"
        onClick={() => selectPreset(preset.id)}
        className={cn(
          'relative flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-lg border-2 transition-all duration-200 text-left w-full',
          isActive
            ? 'border-amber-500/70 bg-amber-500/10'
            : 'border-border-subtle bg-dark-elevated-1 hover:border-border-strong hover:bg-dark-elevated-2',
        )}
      >
        {/* Active indicator — left bar */}
        {isActive && (
          <div className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-amber-500" />
        )}
        <span
          className={cn(
            'text-sm font-semibold',
            isActive ? 'text-amber-400' : 'text-text-primary',
          )}
        >
          {preset.label}
        </span>
        <span className="text-[11px] text-text-muted leading-snug">
          {preset.hint}
        </span>
      </button>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <AnnotationPanel
      title="Axis Alignment"
      subtitle="Score body axis alignment"
      icon={<ArrowUpIcon className="w-5 h-5" />}
      accentColor="amber"
      panelNumber={panelNumber}
      totalPanels={totalPanels}
      isExpanded={isExpanded}
      onToggle={onToggle}
      label={label}
      onLabelChange={setLabel}
      notes={notes}
      onNotesChange={setNotes}
      importance={importance}
      onImportanceChange={setImportance}
      canSave={canSave}
      isSaving={isSaving}
      isSaved={localIsSaved || isSaved}
      onSave={handleSave}
      saveButtonText="Save Axis Goal"
      className={cn(className, isSelectionActive && 'opacity-50 pointer-events-none')}
    >
      {/* ─── 1. Preset Selection ─────────────────────────────────────── */}
      <div className="space-y-2 mb-4">
        {/* Universal presets — 3 columns */}
        <div className="grid grid-cols-3 gap-1.5">
          {PRESETS.filter((p) => ['torso_lean', 'trunk_line', 'plumb_line'].includes(p.id)).map(renderPresetButton)}
        </div>

        {/* Combat presets — 2 columns */}
        <div className="grid grid-cols-2 gap-1.5">
          {PRESETS.filter((p) => ['hip_shoulder_separation', 'arm_extension'].includes(p.id)).map(renderPresetButton)}
        </div>

        {/* Stance presets — 3 columns */}
        <div className="grid grid-cols-3 gap-1.5">
          {PRESETS.filter((p) => ['hip_level', 'shoulder_level', 'foot_level'].includes(p.id)).map(renderPresetButton)}
        </div>

        {/* Rotation / 3D + Custom — 2 columns */}
        <div className="grid grid-cols-2 gap-1.5">
          {PRESETS.filter((p) => ['stance_orientation', 'custom'].includes(p.id)).map(renderPresetButton)}
        </div>
      </div>

      {/* ─── 2. Rotation Mode Settings ───────────────────────────────── */}
      {isRotationMode && (
        <div
          className="mb-4 space-y-4 p-3 rounded-lg bg-dark-elevated-1 border border-border-subtle"
          data-testid="rotation-mode-settings"
        >
          {/* Header */}
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="text-xs font-semibold text-amber-400">3D Body Rotation Measurement</span>
          </div>

          {/* SO Library Picker — only shown for the stance_orientation preset */}
          {activePreset === 'stance_orientation' && (
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">
                Apply stance profile
              </label>
              <select
                data-testid="so-library-select"
                defaultValue=""
                onChange={(e) => {
                  const entry = SO_LIBRARY.find((s) => s.id === e.target.value);
                  if (!entry) return;
                  setRotationTolerance(entry.angle_tolerance_degrees);
                  setRotationIsGate(entry.is_gate);
                  setImportance(soImportanceToSlider(entry.importance));
                }}
                className="w-full px-3 py-2 border border-border-subtle bg-dark-elevated-1 text-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/40 text-sm"
              >
                <option value="" disabled>Choose a profile…</option>
                {SO_LIBRARY.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.label} — ±{entry.angle_tolerance_degrees}°{entry.is_gate ? ' — hard limit' : ''}
                  </option>
                ))}
              </select>
              {/* "Sets tolerance, gate, and importance" named three internal
                  fields and told the coach nothing they could act on
                  (docs/COACH_FACING_COPY_STANDARD.md §2). It also stopped short
                  of the one thing a profile does NOT fill in — the failure
                  angle, which only the coach can set (§C.3c). */}
              <p className="mt-1 text-[10px] text-text-muted">
                Sets how strict this check is, whether it&apos;s a hard limit, and how much it
                counts. Your other body-facing settings stay as they are, and you still choose the
                angle that counts as a failure.
              </p>
            </div>
          )}

          {/* Reference type */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Scoring Method
            </label>
            <select
              value={rotationReferenceType}
              onChange={(e) => setRotationReferenceType(e.target.value as 'template_angle' | 'fixed_angle')}
              className="w-full px-3 py-2 border border-border-subtle bg-dark-elevated-1 text-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/40 text-sm"
            >
              <option value="template_angle">Match Template</option>
              <option value="fixed_angle">Fixed Angle</option>
            </select>
          </div>

          {/* Fixed angle target — only shown for fixed_angle mode */}
          {rotationReferenceType === 'fixed_angle' && (
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">
                Target Rotation Angle: {rotationTargetAngle}°
              </label>
              <input
                type="range"
                min="-90"
                max="90"
                step="5"
                value={rotationTargetAngle}
                onChange={(e) => setRotationTargetAngle(parseFloat(e.target.value))}
                className="w-full h-1.5 bg-gradient-to-r from-amber-900/60 via-amber-600/40 to-amber-400/60 rounded-full appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-[10px] text-text-muted mt-1">
                <span>-90° facing camera</span>
                <span>0° profile</span>
                <span>+90° facing camera</span>
              </div>
            </div>
          )}

          {/* Tolerance */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Tolerance: {rotationTolerance}°
            </label>
            <input
              type="range"
              min="5"
              max="45"
              step="5"
              value={rotationTolerance}
              onChange={(e) => setRotationTolerance(parseFloat(e.target.value))}
              className="w-full h-1.5 bg-gradient-to-r from-amber-900/60 via-amber-600/40 to-amber-400/60 rounded-full appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-text-muted mt-1">
              <span>5° strict</span>
              <span>45° lenient</span>
            </div>
          </div>

          {/* Hard-limit checkbox — Stance Orientation only.
              Label must NOT mention "rotation_profile", degrees, or statistics.
              When enabled, emits rotation_profile=true in the saved AxisConfig
              and reveals PR-5's failure-angle field below. */}
          <div>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                data-testid="stance-orientation-gate-toggle"
                checked={rotationIsGate}
                onChange={(e) => setRotationIsGate(e.target.checked)}
                className="mt-0.5 w-3.5 h-3.5 text-amber-500 border-border-strong rounded focus:ring-amber-500/40"
              />
              <div className="space-y-0.5">
                <span className="text-xs font-medium text-text-primary block">
                  {HARD_LIMIT_TOGGLE_LABEL}
                </span>
                <span className="text-[10px] text-text-muted block">
                  {HARD_LIMIT_TOGGLE_EXPLANATION}
                </span>
              </div>
            </label>
          </div>

          {/* PR-5 — the failure angle, asked for only once this check IS a hard
              limit. It is a different number from the strictness above: that one
              says how far off still scores well, this one says past which the
              attempt is lost. Writing `coach_breach_degrees` is the only thing
              this control does; the shape and the threshold source are the
              backend's (§C.3c). */}
          {rotationIsGate && (
            <GateBreachControl
              breachDegrees={rotationBreachDegrees}
              gateSummary={gateSummary}
              onChange={(degrees) =>
                onAxisConfigChange({
                  ...(axisConfig || defaultAxisConfig),
                  coach_breach_degrees: degrees,
                })
              }
            />
          )}

          {/* Goal F (EXP-010 A7b, step F4) — mismatch warning. Shown ONLY
              when both numbers exist and disagree; this failure angle keeps
              deciding the score while the hard limit stays on (§C.3c /
              CLAUDE.md §11) — this is a nudge to keep the typed pricing
              limit in sync, not a second control. */}
          {rotationIsGate && pricingLimitMismatch && (
            <div
              data-testid="pricing-limit-mismatch-warning"
              className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30"
            >
              <p className="text-[11px] text-amber-300 leading-relaxed">
                This check&apos;s failure angle ({rotationBreachDegrees}°) is different from the
                limit you typed for body position elsewhere ({pricingOrientationLimitDegrees}°).
                While this hard limit is on, the number above is the one that decides the score —
                update either one so they match what you mean.
              </p>
            </div>
          )}

          {/* Contextual hint */}
          <div className="px-3 py-2 rounded-lg bg-amber-500/6 border border-amber-500/15">
            <p className="text-[11px] text-text-secondary leading-relaxed">
              {rotationReferenceType === 'template_angle'
                ? "The athlete's body rotation will be scored against the template's rotation trajectory frame-by-frame."
                : rotationTargetAngle === 0
                  ? 'Score against a fixed profile-to-camera orientation (one shoulder toward the lens). Set target angle to match the desired body orientation.'
                  : `Score against a fixed ${Math.abs(rotationTargetAngle)}° torso rotation from profile-to-camera (90° = chest or back toward the lens).`}
            </p>
          </div>
        </div>
      )}

      {/* ─── 3. Status / Custom Axis Instructions ────────────────────── */}
      {isCustomMode || (anchorMode && activePreset !== 'torso_lean') ? (
        /* Custom axis: step-by-step placement */
        <div className="mb-4 space-y-2">
          <div className="p-3 rounded-lg bg-dark-elevated-1 border border-border-subtle">
            <div className="space-y-2">
              {/* Step 1 */}
              <div className="flex items-center gap-2.5">
                <span
                  className={cn(
                    'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors',
                    anchors?.start
                      ? 'bg-amber-500 text-white'
                      : 'border-2 border-amber-500/40 text-amber-500/60',
                    !anchors?.start && (pendingAnchorPoint || anchors === null) && 'animate-pulse',
                  )}
                >
                  {anchors?.start ? '✓' : '1'}
                </span>
                <span
                  className={cn(
                    'text-xs',
                    anchors?.start ? 'text-text-primary' : 'text-text-secondary',
                  )}
                >
                  {anchors?.start ? 'Start point placed' : 'Click on video to set start point'}
                </span>
              </div>

              {/* Step 2 */}
              <div className="flex items-center gap-2.5">
                <span
                  className={cn(
                    'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors',
                    anchors?.end
                      ? 'bg-amber-500 text-white'
                      : anchors?.start
                        ? 'border-2 border-amber-500/40 text-amber-500/60 animate-pulse'
                        : 'border-2 border-border-subtle text-text-muted',
                  )}
                >
                  {anchors?.end ? '✓' : '2'}
                </span>
                <span
                  className={cn(
                    'text-xs',
                    anchors?.end
                      ? 'text-text-primary'
                      : anchors?.start
                        ? 'text-text-secondary'
                        : 'text-text-muted',
                  )}
                >
                  {anchors?.end
                    ? 'End point placed'
                    : anchors?.start
                      ? 'Click on video to set end point'
                      : 'Set start point first'}
                </span>
              </div>
            </div>
          </div>

          {/* Reset */}
          {(anchors?.start || anchors?.end) && onResetAnchors && (
            <button
              type="button"
              onClick={onResetAnchors}
              className="w-full px-3 py-1.5 text-xs font-medium text-text-secondary bg-dark-elevated-1 border border-border-subtle rounded-lg hover:text-text-primary hover:border-border-strong transition-colors"
            >
              Reset Points
            </button>
          )}

          {/* Both placed — confirmation */}
          {anchorsReady && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/8 border border-amber-500/20">
              <svg className="w-4 h-4 text-amber-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-xs text-amber-400 font-medium">
                Axis defined — visible on video
              </span>
            </div>
          )}
        </div>
      ) : activePreset && (landmarksReady || isRotationMode) ? (
        /* Preset active and configured — confirmation strip */
        <div className="mb-4 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-amber-500/8 border border-amber-500/20">
          <svg className="w-4 h-4 text-amber-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-xs text-amber-400 font-medium">
            {isRotationMode ? 'Rotation goal configured' : 'Axis active — visible on video'}
          </span>
        </div>
      ) : !activePreset ? (
        /* No preset selected yet */
        <div className="mb-4 px-3 py-2.5 rounded-lg bg-dark-elevated-1 border border-border-subtle">
          <span className="text-xs text-text-muted">
            Choose an axis type above to get started
          </span>
        </div>
      ) : null}

      {/* ─── 4. Scoring Settings (collapsible, not shown in rotation mode) ─── */}
      {hasCompleteAxisConfig && !isRotationMode && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowSettings(!showSettings)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-dark-elevated-1 border border-border-subtle hover:border-border-strong transition-colors"
          >
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
              Scoring Settings
            </span>
            <ChevronDownIcon
              className={cn(
                'w-3.5 h-3.5 text-text-muted transition-transform duration-200',
                showSettings && 'rotate-180',
              )}
            />
          </button>

          {showSettings && (
            <div className="mt-2 space-y-4 p-3 rounded-lg bg-dark-elevated-1/50 border border-border-subtle">
              {/* Reference Type */}
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1.5">
                  Scoring Method
                </label>
                <select
                  value={axisConfig?.reference_type || 'vertical'}
                  onChange={(e) => updateAxisConfig('reference_type', e.target.value)}
                  className="w-full px-3 py-2 border border-border-subtle bg-dark-elevated-1 text-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/40 text-sm"
                >
                  <option value="vertical">Compare to vertical</option>
                  <option value="horizontal">Compare to horizontal</option>
                  <option value="template_angle">Match template angle</option>
                  <option value="initial_angle">Maintain starting angle</option>
                </select>
              </div>

              {/* Target Angle — for vertical and horizontal */}
              {(axisConfig?.reference_type === 'vertical' || axisConfig?.reference_type === 'horizontal') && (
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">
                    Target Angle: {axisConfig?.target_angle_degrees || 0}°
                  </label>
                  <input
                    type="range"
                    min="-45"
                    max="45"
                    step="5"
                    value={axisConfig?.target_angle_degrees || 0}
                    onChange={(e) => updateAxisConfig('target_angle_degrees', parseFloat(e.target.value))}
                    className="w-full h-1.5 bg-gradient-to-r from-amber-900/60 via-amber-600/40 to-amber-400/60 rounded-full appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-text-muted mt-1">
                    <span>-45°</span>
                    <span>0° vertical</span>
                    <span>+45°</span>
                  </div>
                </div>
              )}

              {/* Measured template angle — shown when reference_type === 'template_angle' */}
              {axisConfig?.reference_type === 'template_angle' && (
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/6 px-3 py-2.5 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-[11px] font-semibold text-amber-400 uppercase tracking-wider">
                      Measured Template Angle
                    </span>
                  </div>

                  {measuredAngle !== null ? (
                    <>
                      <p className="text-xs text-text-primary">
                        <span className="font-semibold text-amber-300">
                          {measuredAngle.meanAngleDeg > 0
                            ? `${measuredAngle.meanAngleDeg}° right of vertical`
                            : measuredAngle.meanAngleDeg < 0
                              ? `${Math.abs(measuredAngle.meanAngleDeg)}° left of vertical`
                              : '0° — plumb vertical'}
                        </span>
                        {' '}across {measuredAngle.validFrameCount} frame{measuredAngle.validFrameCount !== 1 ? 's' : ''}
                        {currentPhase ? ` in phase "${currentPhase.name}"` : ''}
                      </p>
                      {!measuredAngle.meetsMinFrames && (
                        <p className="text-[11px] text-amber-500 font-medium">
                          Warning: fewer than 3 valid frames — the evaluator will fall back to{' '}
                          {axisConfig.target_angle_degrees}° at scoring time. Extend the phase
                          range or check that both landmarks are visible.
                        </p>
                      )}
                      <p className="text-[10px] text-text-muted">
                        Indicative — authoritative angle is computed at eval time.
                      </p>
                    </>
                  ) : (axisConfig?.landmark_start?.startsWith('lead_') ||
                       axisConfig?.landmark_start?.startsWith('trail_') ||
                       axisConfig?.landmark_end?.startsWith('lead_') ||
                       axisConfig?.landmark_end?.startsWith('trail_')) ? (
                    <p className="text-[11px] text-text-secondary">
                      Functional landmarks resolve at eval time — the measured angle
                      will be computed from the athlete's actual lead/trail side.
                    </p>
                  ) : !templatePoseFrames || templatePoseFrames.size === 0 ? (
                    <p className="text-[11px] text-text-secondary">
                      Template pose data loading — angle will display once available.
                    </p>
                  ) : (
                    <p className="text-[11px] text-text-secondary">
                      No valid frames found for this landmark pair. Check that both
                      landmarks are tracked in the selected phase.
                    </p>
                  )}
                </div>
              )}

              {/* Strictness — the tolerance review prompt (observability
                  Phase 4). Replaces a bare "Tolerance: 10°" slider that never
                  said whose 10° it was: every live axis goal sat on the client
                  preset equal to the model default (ledger HC-26 / HC-30), so
                  the coach was never asked. */}
              <StrictnessControl
                label={label.trim() || 'this check'}
                degrees={axisConfig?.angle_tolerance_degrees}
                source={axisConfig?.tolerance_source}
                standardDegrees={defaultAxisConfig.angle_tolerance_degrees}
                comparedTo={
                  axisConfig?.reference_type === 'template_angle' ? 'demo' : 'target'
                }
                onChange={(degrees, source) =>
                  onAxisConfigChange({
                    ...(axisConfig || defaultAxisConfig),
                    angle_tolerance_degrees: degrees,
                    tolerance_source: source,
                  })
                }
              />

              {/* Stability */}
              <div>
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={axisConfig?.measure_stability ?? true}
                    onChange={(e) => updateAxisConfig('measure_stability', e.target.checked)}
                    className="w-3.5 h-3.5 text-amber-500 border-border-strong rounded focus:ring-amber-500/40"
                  />
                  <div>
                    <span className="text-xs font-medium text-text-primary">Score stability</span>
                    <span className="text-[10px] text-text-muted ml-1.5">less wobble = better</span>
                  </div>
                </label>
              </div>

              {/* Stability threshold */}
              {(axisConfig?.measure_stability ?? true) && (
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">
                    Max Wobble: {axisConfig?.stability_threshold ?? 5}°
                  </label>
                  <input
                    type="range"
                    min={2}
                    max={20}
                    step={1}
                    value={axisConfig?.stability_threshold ?? 5}
                    onChange={(e) => updateAxisConfig('stability_threshold', parseFloat(e.target.value))}
                    className="w-full h-1.5 bg-gradient-to-r from-amber-900/60 via-amber-600/40 to-amber-400/60 rounded-full appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-text-muted mt-1">
                    <span>2° static</span>
                    <span>20° dynamic</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── 5. Advanced: Manual Landmark Selection (hidden in rotation/custom mode) ─ */}
      {!isCustomMode && !anchorMode && !isRotationMode && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-[11px] text-text-muted hover:text-text-secondary transition-colors"
          >
            {showAdvanced ? 'Hide advanced options' : 'Advanced: choose landmarks manually'}
          </button>

          {showAdvanced && (
            <div className="mt-2 space-y-3 p-3 rounded-lg bg-dark-elevated-1/50 border border-border-subtle">
              <div>
                <label className="block text-[11px] font-medium text-text-secondary mb-1">
                  Start Landmark
                </label>
                <FunctionalLandmarkPicker
                  value={axisConfig?.landmark_start || ''}
                  onChange={(name) => {
                    onAxisConfigChange({
                      ...(axisConfig || defaultAxisConfig),
                      landmark_start: name,
                    });
                    setActivePreset(null);
                    if (!label || PRESETS.some((p) => p.label === label)) {
                      setLabel('Custom Axis');
                    }
                  }}
                  onClickSkeleton={() => handleRequestLandmarkSelection('landmark_start')}
                  canClickSkeleton={!!onRequestLandmarkSelection}
                  side="A"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-text-secondary mb-1">
                  End Landmark
                </label>
                <FunctionalLandmarkPicker
                  value={axisConfig?.landmark_end || ''}
                  onChange={(name) => {
                    onAxisConfigChange({
                      ...(axisConfig || defaultAxisConfig),
                      landmark_end: name,
                    });
                    setActivePreset(null);
                    if (!label || PRESETS.some((p) => p.label === label)) {
                      setLabel('Custom Axis');
                    }
                  }}
                  onClickSkeleton={() => handleRequestLandmarkSelection('landmark_end')}
                  canClickSkeleton={!!onRequestLandmarkSelection}
                  side="B"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── 6. Axis Purpose (N6e) ────────────────────────────────────── */}
      {/* Soft-require: nudge when empty, never blocks saving. */}
      <div className="mb-3 space-y-2">
        {/* Section header with incentivizing tooltip */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
            Why does this axis matter?
          </span>
          {/* N6e: recommended badge when purpose is empty */}
          {!purposeCategory && !purposeText.trim() && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/30">
              recommended
            </span>
          )}
          <Tooltip
            position="right"
            content={
              <div className="max-w-xs space-y-2 p-0.5">
                <p className="text-[11px] font-semibold text-text-primary">
                  Teaching vs Physical Support
                </p>
                <div className="space-y-1.5">
                  <div>
                    <span className="text-[10px] font-semibold text-amber-400">Teaching:</span>
                    <span className="text-[10px] text-text-secondary ml-1">
                      e.g. a shoulder line lets the athlete SEE whether they&apos;re leaning
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-amber-400">Physical support:</span>
                    <span className="text-[10px] text-text-secondary ml-1">
                      e.g. lead shoulder &rarr; lead hip alignment in boxing ensures body weight transfers legs &rarr; core &rarr; arm &rarr; fist
                    </span>
                  </div>
                </div>
                <p className="text-[10px] text-text-muted border-t border-border-subtle pt-1.5">
                  The AI coach uses your reason to explain deviations in your words — axes with a stated purpose produce far clearer athlete feedback.
                </p>
              </div>
            }
          >
            <button
              type="button"
              aria-label="Learn why axis purpose matters"
              className="text-text-muted hover:text-text-secondary transition-colors"
              data-testid="axis-purpose-tooltip-trigger"
            >
              <InformationCircleIcon className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </div>

        {/* Category selector */}
        <select
          data-testid="axis-purpose-category"
          value={purposeCategory}
          onChange={(e) => setPurposeCategory(e.target.value as 'teaching' | 'biomechanical' | '')}
          className="w-full px-3 py-2 border border-border-subtle bg-dark-elevated-1 text-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/40 text-sm"
        >
          <option value="">Select a purpose type…</option>
          <option value="teaching">Teaching a concept</option>
          <option value="biomechanical">Physical support</option>
        </select>

        {/* Free-text purpose field */}
        <textarea
          data-testid="axis-purpose-text"
          value={purposeText}
          onChange={(e) => setPurposeText(e.target.value)}
          maxLength={300}
          rows={2}
          placeholder={
            purposeCategory === 'teaching'
              ? 'e.g. this shoulder line helps the athlete see if they\'re leaning sideways'
              : purposeCategory === 'biomechanical'
                ? 'e.g. lead shoulder to hip alignment ensures the kinetic chain can transfer power'
                : 'Explain why this axis matters for this specific movement…'
          }
          className="w-full px-3 py-2 border border-border-subtle bg-dark-elevated-1 text-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/40 text-xs resize-none placeholder:text-text-muted"
        />
        {purposeText.length > 250 && (
          <p className="text-[10px] text-text-muted text-right">
            {purposeText.length}/300
          </p>
        )}
      </div>

      {/* ─── 7. Phase Context ────────────────────────────────────────── */}
      {currentPhase && (
        <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500/8 border border-amber-500/15 w-fit">
          <span className="text-[10px] font-semibold text-amber-500/70 uppercase tracking-wider">Phase</span>
          <span className="text-xs text-amber-400 font-medium">{currentPhase.name}</span>
        </div>
      )}
    </AnnotationPanel>
  );
}
